import React, { useMemo, useCallback } from "react";
import { Box, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import DataTable from "../../components/DataTable";
import { createTableColumns } from "./tableConfig";

interface SessionDisplay extends Record<string, unknown> {
  id: number;
  provider: {
    id?: number;
    name?: string;
    avatarUrl?: string;
  };
  patient: string;
  patientId: number;
  patientUid: string;
  type: string;
  date: string;
  treatmentPlan?: {
    id: number;
    name: string;
  };
}

interface SessionsTableProps {
  sessions: SessionDisplay[];
  sortField: string;
  sortDirection: "asc" | "desc";
  currentPage: number;
  pageSize: number;
  totalCount: number;
  loading: boolean;
  error: string | null;
  showProviderColumn: boolean;
  onSort: (field: string, direction: "asc" | "desc") => void;
  onPageChange: (page: number) => void;
  onSessionClick: (sessionId: number) => void;
  onPatientClick: (patientId: number) => void;
  onProviderClick: (providerId: number) => void;
  onTreatmentPlanClick: (planId: number) => void;
  onRetry: () => void;
}

const SessionsTable: React.FC<SessionsTableProps> = ({
  sessions,
  sortField,
  sortDirection,
  currentPage,
  pageSize,
  totalCount,
  loading,
  error,
  showProviderColumn,
  onSort,
  onPageChange,
  onSessionClick,
  onPatientClick,
  onProviderClick,
  onTreatmentPlanClick,
  onRetry,
}) => {
  const { t } = useTranslation();

  const formatDate = useCallback((isoDate: string) => {
    const date = new Date(isoDate);
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const year = date.getFullYear();
    return `${month}/${day}/${year}`; // MM/DD/YYYY
  }, []);

  const columns = useMemo(
    () =>
      createTableColumns({
        formatDate,
        noPlanAvailableText: t("allSessions.noPlanAvailable"),
        sessionDetailsText: t("allSessions.sessionDetails"),
        showProviderColumn,
        providerColumnTitle: t("allSessions.columns.provider"),
        patientColumnTitle: t("allSessions.columns.patient"),
        typeColumnTitle: t("allSessions.columns.type"),
        dateColumnTitle: t("allSessions.columns.date"),
        planColumnTitle: t("allSessions.columns.plan"),
        actionsColumnTitle: t("allSessions.columns.actions"),
        onSessionClick,
        onPatientClick,
        onProviderClick,
        onTreatmentPlanClick,
      }),
    [
      t,
      formatDate,
      showProviderColumn,
      onSessionClick,
      onPatientClick,
      onProviderClick,
      onTreatmentPlanClick,
    ],
  );

  if (error) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "400px",
          gap: 2,
        }}
      >
        <Typography color="error">{error}</Typography>
        <button onClick={onRetry}>Retry</button>
      </Box>
    );
  }

  return (
    <DataTable<SessionDisplay>
      columns={columns}
      data={sessions}
      loading={loading}
      sortField={sortField}
      sortDirection={sortDirection}
      onSort={onSort}
      pageSize={pageSize}
      currentPage={currentPage}
      onPageChange={onPageChange}
      totalCount={totalCount}
      emptyMessage={t("allSessions.noSessionsFound")}
    />
  );
};

export default React.memo(SessionsTable);
